import {
  CardInstance,
  CardDef,
  MatchState,
  BattlePlayerState,
  FieldState,
  BattleAction,
  MatchResult,
  FANS_PER_ROUND,
  CardCategory,
  BattleLogEntry,
  AbilityCondition,
} from '../types';
import { getCardById, ALL_CARDS } from '../cards';

// 簡易ID生成
function generateId(): string {
  return Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
}

// ベンチスロット (同名カードは重ねる)
interface BenchSlot {
  cards: CardInstance[];  // 同名カードのスタック
  defId: string;
}

// 内部用フィールド拡張型
interface InternalFieldState extends FieldState {
  _deck: CardInstance[];
  _benchSlots: BenchSlot[];  // ベンチは6スロット、同名カードは重ねる
  _attackStack: CardInstance[];  // アタック側が積み上げたカード
  _pendingDraws: number;
  _nextPowerMod: number;
}

// 山札操作用のコールバック(UI連携用)
export type SwapMode = 'reorder' | 'select' | 'top' | 'bottom';

export interface SwapUICallback {
  showSwapUI: (playerId: string, cards: CardInstance[], count: number, onComplete: (ordered: CardInstance[]) => void, mode?: SwapMode) => void;
}

export interface MoveUICallback {
  showMoveUI: (playerId: string, cards: CardInstance[], maxSelect: number, title: string, description: string, onComplete: (selected: CardInstance[]) => void) => void;
}

// マッチ処理クラス
export class MatchPhase {
  private state: MatchState;
  private cardDefs: Map<string, CardDef>;
  private readonly BENCH_SLOT_LIMIT = 6;
  private swapCallback?: SwapUICallback;
  private moveCallback?: MoveUICallback;
  private isWaitingForSwap: boolean = false;
  private isWaitingForMove: boolean = false;
  private pendingSwapResolve: (() => void) | null = null;

  constructor(
    round: number,
    player1Id: string,
    player1Deck: CardInstance[],
    player2Id: string,
    player2Deck: CardInstance[],
    firstPlayerId?: string,  // 先行プレイヤーID (指定なしならコイントス)
    skipShuffle: boolean = false  // マルチプレイでサーバーから受信した場合はシャッフルスキップ
  ) {
    // シャッフルスキップ時はそのまま使用 (マルチプレイ同期用)
    const deck1 = skipShuffle ? [...player1Deck] : this.shuffleDeck([...player1Deck]);
    const deck2 = skipShuffle ? [...player2Deck] : this.shuffleDeck([...player2Deck]);

    // カード定義マップ作成
    this.cardDefs = new Map(ALL_CARDS.map((c) => [c.id, c]));

    // 先行決定
    let isPlayer1First: boolean;
    if (firstPlayerId) {
      isPlayer1First = firstPlayerId === player1Id;
    } else {
      isPlayer1First = Math.random() < 0.5;
    }

    this.state = {
      matchId: generateId(),
      round,
      status: 'waiting',
      players: [
        this.createBattlePlayerState(player1Id, deck1, !isPlayer1First),
        this.createBattlePlayerState(player2Id, deck2, isPlayer1First),
      ],
      turnCount: 0,
      log: [],
    };
  }

  private createBattlePlayerState(
    playerId: string,
    deck: CardInstance[],
    isAttacker: boolean
  ): BattlePlayerState {
    return {
      playerId,
      field: {
        flagHolder: null,
        challenger: null,
        bench: [],
        exile: [],
        deckRemaining: deck.length,
        _deck: deck,
        _benchSlots: [],
        _attackStack: [],
        _pendingDraws: 0,
        _nextPowerMod: 0,
      } as InternalFieldState,
      isAttacker,
    };
  }

  private shuffleDeck(deck: CardInstance[]): CardInstance[] {
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    return deck;
  }

  setSwapCallback(callback: SwapUICallback): void {
    this.swapCallback = callback;
  }

  setMoveCallback(callback: MoveUICallback): void {
    this.moveCallback = callback;
  }

  // 入力待機中かどうか
  isWaitingForInput(): boolean {
    return this.isWaitingForSwap || this.isWaitingForMove;
  }

  private getInternalField(playerIdx: number): InternalFieldState {
    return this.state.players[playerIdx].field as InternalFieldState;
  }

  // マッチ開始: 先行がカード1枚めくってフラッグを置く
  start(): MatchState {
    this.state.status = 'inProgress';
    
    // 先行プレイヤー (isAttacker=falseの方が先行でフラッグを置く)
    const firstIdx = this.state.players[0].isAttacker ? 1 : 0;
    const secondIdx = firstIdx === 0 ? 1 : 0;
    
    // マッチ開始ログ
    const p1 = this.state.players[0];
    const p2 = this.state.players[1];
    console.log(`[Battle] ========== MATCH START ==========`);
    console.log(`[Battle] P1: ${p1.playerId.slice(-4)} deck=${this.getInternalField(0)._deck.length}`);
    console.log(`[Battle] P2: ${p2.playerId.slice(-4)} deck=${this.getInternalField(1)._deck.length}`);
    console.log(`[Battle] First: P${firstIdx + 1} (${this.state.players[firstIdx].playerId.slice(-4)})`);
    console.log(`[Battle] ===================================`);
    
    const firstCard = this.drawCard(firstIdx);
    if (firstCard) {
      this.setFlagHolder(firstIdx, firstCard);
      this.addLog(firstIdx, 'reveal', firstCard.instanceId,
        `先行: ${this.getCardName(firstCard)}(パワー${this.getEffectiveFlagPower(firstCard)})にフラッグを設置`);
      
      // 最初のカードでも効果を発動(onPlay, onDeckCheck)
      this.triggerAbility(firstIdx, firstCard, 'onPlay');
      this.triggerDeckCheck(firstIdx, firstCard);
    }

    return this.state;
  }

  // カードを1枚引く
  private drawCard(playerIdx: number): CardInstance | null {
    const field = this.getInternalField(playerIdx);
    
    if (field._deck.length === 0) {
      return null;
    }

    const card = field._deck.shift()!;
    card.isRevealed = true;
    field.deckRemaining = field._deck.length;

    // 前ターンからのパワー修正を適用
    if (field._nextPowerMod !== 0) {
      card.currentPower += field._nextPowerMod;
      this.addLog(playerIdx, 'abilityTrigger', card.instanceId,
        `パワー修正: ${field._nextPowerMod > 0 ? '+' : ''}${field._nextPowerMod}`);
      field._nextPowerMod = 0;
    }

    return card;
  }

  // フラッグ保持カードの実効パワー (特殊効果込み)
  private getEffectiveFlagPower(card: CardInstance): number {
    const def = this.cardDefs.get(card.defId);
    let power = card.currentPower;

    // フラッグ保持時のパワーブースト能力をチェック
    if (def) {
      for (const ability of def.abilities) {
        if (ability.timing === 'onFlag' && ability.effect.type === 'powerBoost') {
          power += ability.effect.value ?? 0;
        }
      }
    }

    return power;
  }

  // フラッグをセット
  private setFlagHolder(playerIdx: number, card: CardInstance): void {
    const player = this.state.players[playerIdx];
    const field = this.getInternalField(playerIdx);
    
    player.field.flagHolder = card;
    // フラッグになったカードをスタックから除外(他のカードは残す)
    field._attackStack = field._attackStack.filter(c => c.instanceId !== card.instanceId);
    player.isAttacker = false;
    
    const opponentIdx = playerIdx === 0 ? 1 : 0;
    this.state.players[opponentIdx].isAttacker = true;

    // onFlag能力発動
    this.triggerAbility(playerIdx, card, 'onFlag');
  }

  // ベンチにカードを送る (同名カードは重ねる)
  private toBench(playerIdx: number, cards: CardInstance[]): boolean {
    const field = this.getInternalField(playerIdx);

    for (const card of cards) {
      // onExit能力発動
      this.triggerAbility(playerIdx, card, 'onExit');

      // 同名スロットを探す
      const existingSlot = field._benchSlots.find((s) => s.defId === card.defId);
      
      if (existingSlot) {
        // 同名カードは重ねる (スロットを圧迫しない)
        existingSlot.cards.push(card);
        this.addLog(playerIdx, 'toBench', card.instanceId,
          `${this.getCardName(card)}をベンチに重ねた`);
      } else {
        // 新規スロット
        if (field._benchSlots.length >= this.BENCH_SLOT_LIMIT) {
          // ベンチ満杯で敗北
          return false;
        }
        field._benchSlots.push({ cards: [card], defId: card.defId });
        this.addLog(playerIdx, 'toBench', card.instanceId,
          `${this.getCardName(card)}がベンチへ (${field._benchSlots.length}/${this.BENCH_SLOT_LIMIT})`);
      }

      // 互換性のためbench配列も更新
      field.bench = field._benchSlots.flatMap((s) => s.cards);
    }

    return true;
  }

  // アタック側のパワーを計算(スタック全体の累積)
  private getAttackStackPower(playerIdx: number): number {
    const field = this.getInternalField(playerIdx);
    return field._attackStack.reduce((sum, card) => sum + card.currentPower, 0);
  }

  // 1ターン進行 (アタック側が1枚めくる)
  processTurn(): MatchState {
    if (this.state.status !== 'inProgress') {
      return this.state;
    }

    this.state.turnCount++;

    const attackerIdx = this.state.players[0].isAttacker ? 0 : 1;
    const defenderIdx = attackerIdx === 0 ? 1 : 0;
    const attackerField = this.getInternalField(attackerIdx);
    const defenderField = this.getInternalField(defenderIdx);

    const defender = this.state.players[defenderIdx];
    const flagCard = defender.field.flagHolder;
    
    // ターン開始ログ
    const flagPowerDisplay = flagCard ? this.getEffectiveFlagPower(flagCard) : 0;
    const flagName = flagCard ? this.getCardName(flagCard) : 'none';
    console.log(`[Battle] --- Turn ${this.state.turnCount} ---`);
    console.log(`[Battle] Attacker: P${attackerIdx + 1} deck=${attackerField._deck.length} stack=${attackerField._attackStack.length}`);
    console.log(`[Battle] Defender: P${defenderIdx + 1} flag=${flagName}(${flagPowerDisplay})`);

    if (!flagCard) {
      // フラッグがない場合 (通常起こらない)
      return this.state;
    }

    // カードを1枚引く
    const drawnCard = this.drawCard(attackerIdx);

    if (drawnCard === null) {
      // デッキ切れで敗北
      this.endMatch(defenderIdx, 'deckout');
      return this.state;
    }

    // onPlay能力発動(プレイ時) - ジャグラー等
    this.triggerAbility(attackerIdx, drawnCard, 'onPlay');
    
    // onDeckCheck能力発動(山札チェック) - リポーター等
    this.triggerAbility(attackerIdx, drawnCard, 'onDeckCheck');
    
    // onBenchCheck能力発動(ベンチ確認) - マーマン等
    this.triggerBenchCheck(attackerIdx, drawnCard);
    
    // onEnter能力発動(旧互換)
    this.triggerAbility(attackerIdx, drawnCard, 'onEnter');

    // ベンチのonBench効果を適用(カードがスタックに積まれる前)
    this.applyBenchEffects(attackerIdx, drawnCard);

    // アタックスタックに追加
    attackerField._attackStack.push(drawnCard);

    const stackPower = this.getAttackStackPower(attackerIdx);
    const flagPower = this.getEffectiveFlagPower(flagCard);

    this.addLog(attackerIdx, 'reveal', drawnCard.instanceId,
      `${this.getCardName(drawnCard)}(パワー${drawnCard.currentPower})を公開 → 累積パワー: ${stackPower}`);

    // フラッグ奪取判定: 累積パワー >= フラッグ保持カードのパワー
    if (stackPower >= flagPower) {
      // フラッグ奪取成功!
      this.addLog(attackerIdx, 'flagTaken', drawnCard.instanceId,
        `フラッグ奪取! ${stackPower} >= ${flagPower}`);

      // 敗者側: フラッグ保持カード + アタックスタックの全カードをベンチへ
      const defenderCardsToMove = [flagCard, ...defenderField._attackStack];
      defenderField._attackStack = []; // アタックスタッククリア
      
      const canBenchDefender = this.toBench(defenderIdx, defenderCardsToMove);
      if (!canBenchDefender) {
        this.endMatch(attackerIdx, 'benchFull');
        return this.state;
      }

      // 勝者側: スタックの一番上のカードがフラッグを獲得
      // 他のカードはスタックに残したまま(次に負けた時にベンチへ)
      const newFlagCard = attackerField._attackStack[attackerField._attackStack.length - 1];

      // フラッグをセット (攻守交代)
      this.setFlagHolder(attackerIdx, newFlagCard);
    }

    // 追加ドロー処理
    const extraDraws = attackerField._pendingDraws;
    attackerField._pendingDraws = 0;
    
    for (let i = 0; i < extraDraws; i++) {
      // 再帰的にターン処理 (簡易版: 追加ドローは即時処理)
      const extraCard = this.drawCard(attackerIdx);
      if (extraCard) {
        this.triggerAbility(attackerIdx, extraCard, 'onEnter');
        attackerField._attackStack.push(extraCard);
      }
    }

    return this.state;
  }

  // デッキ確認時の能力発動(onPlay, onDeckCheck)
  private triggerDeckCheck(playerIdx: number, card: CardInstance): void {
    const def = this.cardDefs.get(card.defId);
    if (!def) return;

    // onDeckCheckタイミングの能力を発動
    const deckCheckAbilities = def.abilities.filter((a) => a.timing === 'onDeckCheck' || a.timing === 'onPlay');
    for (const ability of deckCheckAbilities) {
      if (!this.checkCondition(playerIdx, ability.effect.condition)) {
        continue;
      }

      if (ability.effect.type === 'swap') {
        this.handleSwap(playerIdx, ability.effect.value ?? 3, (ability.effect.swapMode ?? 'reorder') as 'reorder' | 'select' | 'discard' | 'top' | 'bottom');
      }
    }
  }

  // 能力発動
  private triggerAbility(
    playerIdx: number,
    card: CardInstance,
    timing: 'onEnter' | 'onExit' | 'onFlag' | 'onPlay' | 'onDeckCheck'
  ): void {
    const def = this.cardDefs.get(card.defId);
    if (!def) return;

    const opponentIdx = playerIdx === 0 ? 1 : 0;
    const abilities = def.abilities.filter((a) => a.timing === timing);
    
    for (const ability of abilities) {
      // 条件チェック
      if (!this.checkCondition(playerIdx, ability.effect.condition)) {
        continue;
      }

      this.addLog(playerIdx, 'abilityTrigger', card.instanceId,
        `[${def.name}] ${ability.effect.description}`);

      const field = this.getInternalField(playerIdx);
      const opponentField = this.getInternalField(opponentIdx);

      switch (ability.effect.type) {
        case 'powerBoost':
          this.handlePowerBoost(playerIdx, card, ability.effect.value ?? 0, timing);
          break;

        case 'benchExpand':
          // ベンチ拡張は現在未実装 (6スロット固定)
          break;

        case 'benchBoost':
          // ベンチブーストはpassiveとして別途処理
          break;

        case 'discard':
          this.handleDiscard(opponentIdx, ability.effect.value ?? 1);
          break;

        case 'revive':
          this.handleRevive(playerIdx, card, def.category);
          break;

        case 'synergy':
          this.handleSynergy(playerIdx, card, ability.effect.value ?? 1, ability.effect.targetCategory);
          break;

        case 'steal':
          this.handleSteal(playerIdx, opponentIdx);
          break;

        case 'swap':
          this.handleSwap(playerIdx, ability.effect.value ?? 3, (ability.effect.swapMode ?? 'reorder') as 'reorder' | 'select' | 'discard' | 'top' | 'bottom');
          break;

        case 'move':
          this.handleMove(playerIdx, opponentIdx, ability.effect);
          break;
      }
    }
  }

  // パワーブースト処理
  private handlePowerBoost(
    playerIdx: number,
    card: CardInstance,
    value: number,
    timing: 'onEnter' | 'onExit' | 'onFlag' | 'onPlay' | 'onDeckCheck'
  ): void {
    const field = this.getInternalField(playerIdx);
    const opponentIdx = playerIdx === 0 ? 1 : 0;
    const opponentField = this.getInternalField(opponentIdx);

    if (timing === 'onEnter' || timing === 'onFlag' || timing === 'onPlay' || timing === 'onDeckCheck') {
      // 自身のパワーを変更
      card.currentPower += value;
    } else if (timing === 'onExit') {
      if (value > 0) {
        // 次の味方カードをバフ
        field._nextPowerMod += value;
      } else {
        // 相手の次のカードをデバフ
        opponentField._nextPowerMod += value;
      }
    }
  }

  // カード破棄処理
  private handleDiscard(opponentIdx: number, count: number): void {
    const opponentField = this.getInternalField(opponentIdx);
    
    for (let i = 0; i < count && opponentField._benchSlots.length > 0; i++) {
      const slot = opponentField._benchSlots[opponentField._benchSlots.length - 1];
      const removed = slot.cards.pop();
      
      if (removed) {
        this.addLog(opponentIdx, 'abilityTrigger', removed.instanceId,
          `${this.getCardName(removed)}が除外された`);
      }
      
      // スロットが空になったら削除
      if (slot.cards.length === 0) {
        opponentField._benchSlots.pop();
      }
    }
    
    // bench配列も更新
    opponentField.bench = opponentField._benchSlots.flatMap((s) => s.cards);
  }

  // 復活処理
  private handleRevive(playerIdx: number, card: CardInstance, category: CardCategory): void {
    const field = this.getInternalField(playerIdx);
    
    const def = this.cardDefs.get(card.defId);
    if (def?.id === 'undead_001') {
      // スケルトン: 自身のコピーをデッキ下に
      const newInstance: CardInstance = {
        defId: card.defId,
        instanceId: generateId(),
        currentPower: def.basePower,
        isRevealed: false,
      };
      field._deck.push(newInstance);
      field.deckRemaining = field._deck.length;
      return;
    }

    // ベンチから対象カテゴリーをデッキに戻す
    for (const slot of field._benchSlots) {
      const slotDef = this.cardDefs.get(slot.defId);
      if (slotDef?.category === category && slot.cards.length > 0) {
        const target = slot.cards.pop()!;
        target.isRevealed = false;
        field._deck.push(target);
        field.deckRemaining = field._deck.length;
        
        this.addLog(playerIdx, 'abilityTrigger', target.instanceId,
          `${this.getCardName(target)}がデッキに戻った`);
        
        // スロットが空になったら削除
        if (slot.cards.length === 0) {
          const idx = field._benchSlots.indexOf(slot);
          field._benchSlots.splice(idx, 1);
        }
        
        field.bench = field._benchSlots.flatMap((s) => s.cards);
        break;
      }
    }
  }

  // シナジー処理
  private handleSynergy(
    playerIdx: number,
    card: CardInstance,
    valuePerCard: number,
    targetCategory?: CardCategory | 'all'
  ): void {
    if (!targetCategory) return;

    const field = this.getInternalField(playerIdx);
    
    // ベンチ内の対象カテゴリーをカウント (スロット数ではなくカード枚数)
    let count = 0;
    for (const slot of field._benchSlots) {
      const slotDef = this.cardDefs.get(slot.defId);
      if (slotDef?.category === targetCategory) {
        count += slot.cards.length;
      }
    }

    const totalBoost = count * valuePerCard;
    if (totalBoost > 0) {
      card.currentPower += totalBoost;
      this.addLog(playerIdx, 'abilityTrigger', card.instanceId,
        `シナジー: +${totalBoost}パワー (${targetCategory} x${count})`);
    }
  }

  // カード奪取処理
  private handleSteal(playerIdx: number, opponentIdx: number): void {
    const field = this.getInternalField(playerIdx);
    const opponentField = this.getInternalField(opponentIdx);

    if (opponentField._benchSlots.length === 0) return;

    const slot = opponentField._benchSlots[opponentField._benchSlots.length - 1];
    const stolen = slot.cards.pop();
    
    if (stolen) {
      stolen.isRevealed = false;
      field._deck.push(stolen);
      field.deckRemaining = field._deck.length;
      this.addLog(playerIdx, 'abilityTrigger', stolen.instanceId,
        `${this.getCardName(stolen)}を奪取!`);
      
      // スロットが空になったら削除
      if (slot.cards.length === 0) {
        opponentField._benchSlots.pop();
      }
      
      opponentField.bench = opponentField._benchSlots.flatMap((s) => s.cards);
    }
  }

  // 山札操作(swap)処理
  private handleSwap(playerIdx: number, count: number, mode: 'reorder' | 'select' | 'discard' | 'top' | 'bottom'): void {
    const field = this.getInternalField(playerIdx);
    const playerId = this.state.players[playerIdx].playerId;
    
    if (field._deck.length === 0) return;
    
    // デッキ上からcount枚を取得
    const topCards = field._deck.splice(0, Math.min(count, field._deck.length));
    
    if (topCards.length === 0) return;
    
    // top/bottomモードは選択UIなしで自動処理可能
    if (mode === 'top') {
      // 選んだカードをデッキ上へ(UIで1枚選択)
      if (this.swapCallback) {
        this.isWaitingForSwap = true;
        this.swapCallback.showSwapUI(playerId, topCards, 1, (selected) => {
          const remaining = topCards.filter(c => !selected.some(s => s.instanceId === c.instanceId));
          field._deck.unshift(...selected);
          field._deck.push(...remaining);
          field.deckRemaining = field._deck.length;
          this.isWaitingForSwap = false;
          if (this.pendingSwapResolve) {
            this.pendingSwapResolve();
            this.pendingSwapResolve = null;
          }
        }, 'top');
      } else {
        field._deck.unshift(...topCards);
        field.deckRemaining = field._deck.length;
      }
      return;
    }
    
    if (mode === 'bottom') {
      // 選んだカードをデッキ下へ(UIで1枚選択)
      if (this.swapCallback) {
        this.isWaitingForSwap = true;
        this.swapCallback.showSwapUI(playerId, topCards, 1, (selected) => {
          const remaining = topCards.filter(c => !selected.some(s => s.instanceId === c.instanceId));
          field._deck.unshift(...remaining);
          field._deck.push(...selected);
          field.deckRemaining = field._deck.length;
          this.isWaitingForSwap = false;
          if (this.pendingSwapResolve) {
            this.pendingSwapResolve();
            this.pendingSwapResolve = null;
          }
        }, 'bottom');
      } else {
        field._deck.push(...topCards);
        field.deckRemaining = field._deck.length;
      }
      return;
    }
    
    if (this.swapCallback) {
      // UI連携: プレイヤーに選択させる
      this.isWaitingForSwap = true;
      
      // 選択枚数は実際のカード枚数を超えない
      const actualSelectCount = Math.min(count, topCards.length);
      
      this.swapCallback.showSwapUI(playerId, topCards, actualSelectCount, (orderedCards) => {
        if (mode === 'reorder') {
          // 並べ替え: 選択された順序でデッキ上に戻す
          field._deck.unshift(...orderedCards);
        } else if (mode === 'select') {
          // 選択: 選んだカードをデッキ上に、残りをデッキ下に
          const selected = orderedCards;
          const remaining = topCards.filter(c => !selected.some(s => s.instanceId === c.instanceId));
          field._deck.unshift(...selected);
          field._deck.push(...remaining);
        } else if (mode === 'discard') {
          // 破棄: 選んだカードを除外、残りをデッキ上に
          const toDiscard = orderedCards;
          const remaining = topCards.filter(c => !toDiscard.some(s => s.instanceId === c.instanceId));
          field._deck.unshift(...remaining);
          // 除外ゾーンに追加
          this.toExile(playerIdx, toDiscard);
        }
        
        field.deckRemaining = field._deck.length;
        this.isWaitingForSwap = false;
        
        if (this.pendingSwapResolve) {
          this.pendingSwapResolve();
          this.pendingSwapResolve = null;
        }
      }, mode === 'select' ? 'select' : 'reorder');
    } else {
      // コールバックなし: 自動処理(ランダムに戻す)
      const shuffled = [...topCards].sort(() => Math.random() - 0.5);
      field._deck.unshift(...shuffled);
      field.deckRemaining = field._deck.length;
    }
  }

  // カードを除外ゾーンに移動
  private toExile(playerIdx: number, cards: CardInstance[]): void {
    const player = this.state.players[playerIdx];
    for (const card of cards) {
      player.field.exile.push(card);
      this.addLog(playerIdx, 'abilityTrigger', card.instanceId,
        `${this.getCardName(card)}を除外`);
    }
  }

  // ベンチのonBench効果を適用(カードがスタックに積まれる時)
  private applyBenchEffects(playerIdx: number, card: CardInstance): void {
    const cardDef = this.cardDefs.get(card.defId);
    if (!cardDef) return;

    const opponentIdx = playerIdx === 0 ? 1 : 0;

    // 両プレイヤーのベンチをチェック
    for (let benchOwnerIdx = 0; benchOwnerIdx < 2; benchOwnerIdx++) {
      const benchField = this.getInternalField(benchOwnerIdx);
      
      for (const slot of benchField._benchSlots) {
        const benchCardDef = this.cardDefs.get(slot.defId);
        if (!benchCardDef) continue;

        for (const ability of benchCardDef.abilities) {
          if (ability.timing !== 'onBench') continue;
          if (ability.effect.type !== 'powerBoost') continue;

          // targetPlayer チェック(未指定の場合は'self'=ベンチ持ち主のカードのみ)
          const targetPlayer = ability.effect.targetPlayer ?? 'self';
          if (targetPlayer === 'self' && benchOwnerIdx !== playerIdx) continue;
          if (targetPlayer === 'opponent' && benchOwnerIdx === playerIdx) continue;

          // targetCategory チェック(「全て」または未指定の場合は常にマッチ)
          const targetCat = ability.effect.targetCategory;
          if (targetCat && targetCat !== 'all' && targetCat !== cardDef.category) continue;

          // targetPower チェック(basePowerで判定)
          if (ability.effect.targetPower !== undefined && ability.effect.targetPower !== cardDef.basePower) continue;

          // 効果適用(ベンチにあるカードの枚数分)
          const boostValue = (ability.effect.value ?? 0) * slot.cards.length;
          card.currentPower += boostValue;

          this.addLog(playerIdx, 'abilityTrigger', card.instanceId,
            `${benchCardDef.name}のベンチ効果: ${this.getCardName(card)}のパワー+${boostValue}`);
        }
      }
    }
  }

  // ベンチ確認効果を発動(onBenchCheck) - マーマン等
  private triggerBenchCheck(playerIdx: number, card: CardInstance): void {
    const def = this.cardDefs.get(card.defId);
    if (!def) return;

    const field = this.getInternalField(playerIdx);
    const opponentIdx = playerIdx === 0 ? 1 : 0;
    const opponentField = this.getInternalField(opponentIdx);

    for (const ability of def.abilities) {
      if (ability.timing !== 'onBenchCheck') continue;

      const condition = ability.effect.condition;
      if (!condition) continue;

      // 対象ベンチを決定
      const targetBench = condition.target === 'opponentBench' ? opponentField : field;
      
      // 条件に合致するカード枚数をカウント
      let matchCount = 0;
      
      for (const slot of targetBench._benchSlots) {
        const slotDef = this.cardDefs.get(slot.defId);
        if (!slotDef) continue;
        
        let matches = false;
        
        if (condition.type === 'categoryPresent') {
          // 属性チェック
          const targetCategory = condition.category;
          matches = !targetCategory || targetCategory === 'all' || slotDef.category === targetCategory;
        } else if (condition.type === 'powerMatch') {
          // パワーチェック
          const comparison = condition.comparison ?? 'equal';
          const targetValue = condition.matchValue ?? 0;
          
          if (comparison === 'equal') {
            matches = slotDef.basePower === targetValue;
          } else if (comparison === 'greaterOrEqual') {
            matches = slotDef.basePower >= targetValue;
          } else if (comparison === 'lessOrEqual') {
            matches = slotDef.basePower <= targetValue;
          }
        } else if (condition.type === 'tierMatch') {
          // ティアチェック
          matches = slotDef.tier === condition.tier;
        }
        
        if (matches) {
          matchCount += slot.cards.length;
        }
      }
      
      // 条件判定
      const minCount = condition.minCount ?? 1;
      const isAbsentCondition = minCount === 0;  // minCount=0は「存在しない」条件
      
      let conditionMet = false;
      if (isAbsentCondition) {
        conditionMet = matchCount === 0;
      } else {
        conditionMet = matchCount >= minCount;
      }
      
      if (!conditionMet) continue;

      // 効果発動
      this.addLog(playerIdx, 'abilityTrigger', card.instanceId,
        `[${def.name}] ${ability.effect.description}`);

      // 効果タイプに応じた処理
      if (ability.effect.type === 'powerBoost') {
        // 枚数倍率(countMultiplier)の場合は枚数×効果値
        const multiplier = condition.countMultiplier ? matchCount : 1;
        const boostValue = (ability.effect.value ?? 0) * multiplier;
        card.currentPower += boostValue;
      } else if (ability.effect.type === 'move') {
        // 移動効果(魔術師など)
        this.handleMove(playerIdx, opponentIdx, ability.effect);
      } else if (ability.effect.type === 'revive') {
        // デッキ復帰効果(バンパイアなど) - 自身をデッキに戻す
        this.handleBenchCheckRevive(playerIdx, card, ability.effect.destination ?? 'deckTop');
      }
    }
  }

  // ベンチ確認時の復帰効果(バンパイアなど)
  private handleBenchCheckRevive(playerIdx: number, card: CardInstance, destination: string): void {
    const field = this.getInternalField(playerIdx);
    
    // カードをデッキに戻す(プレイ前なのでスタックにはまだない)
    if (destination === 'deckTop') {
      field._deck.unshift(card);
    } else if (destination === 'deckBottom') {
      field._deck.push(card);
    } else {
      // ランダム位置
      const pos = Math.floor(Math.random() * (field._deck.length + 1));
      field._deck.splice(pos, 0, card);
    }
    field.deckRemaining = field._deck.length;
    
    this.addLog(playerIdx, 'abilityTrigger', card.instanceId,
      `${this.getCardName(card)}をデッキに戻した`);
  }

  // move効果処理
  private handleMove(
    playerIdx: number,
    opponentIdx: number,
    effect: { value?: number; destination?: string; targetPlayer?: string }
  ): void {
    const playerId = this.state.players[playerIdx].playerId;
    const maxSelect = effect.value ?? 1;
    const destination = effect.destination ?? 'exile';
    const targetPlayer = effect.targetPlayer;

    // ベンチからカードを収集(ownerIdx付き)
    interface CardWithOwner extends CardInstance {
      _ownerIdx: number;
    }
    
    const benchCards: CardWithOwner[] = [];
    
    if (targetPlayer === 'opponent') {
      // 相手ベンチのみ
      const opponentField = this.getInternalField(opponentIdx);
      for (const slot of opponentField._benchSlots) {
        for (const card of slot.cards) {
          benchCards.push({ ...card, _ownerIdx: opponentIdx });
        }
      }
    } else if (targetPlayer === 'self') {
      // 自分ベンチのみ
      const myField = this.getInternalField(playerIdx);
      for (const slot of myField._benchSlots) {
        for (const card of slot.cards) {
          benchCards.push({ ...card, _ownerIdx: playerIdx });
        }
      }
    } else {
      // 選択カード: 両方のベンチから選択可能
      const myField = this.getInternalField(playerIdx);
      const opponentField = this.getInternalField(opponentIdx);
      
      for (const slot of myField._benchSlots) {
        for (const card of slot.cards) {
          benchCards.push({ ...card, _ownerIdx: playerIdx });
        }
      }
      for (const slot of opponentField._benchSlots) {
        for (const card of slot.cards) {
          benchCards.push({ ...card, _ownerIdx: opponentIdx });
        }
      }
    }

    if (benchCards.length === 0) {
      this.addLog(playerIdx, 'abilityTrigger', '', 'ベンチにカードがありません');
      return;
    }

    let title = '🎯 ベンチから除外';
    if (targetPlayer === 'opponent') {
      title = '🎯 相手ベンチから除外';
    } else if (targetPlayer === 'self') {
      title = '🎯 自分ベンチから除外';
    }
    const desc = `${maxSelect}枚まで選んで除外`;

    if (this.moveCallback) {
      this.isWaitingForMove = true;
      
      // ownerIdx情報を渡すため、カードにマーキング
      this.moveCallback.showMoveUI(playerId, benchCards, maxSelect, title, desc, (selected) => {
        // ベンチから選択したカードを除外
        for (const card of selected) {
          const ownerIdx = (card as CardWithOwner)._ownerIdx;
          this.removeFromBench(ownerIdx, card);
          this.state.players[ownerIdx].field.exile.push(card);
          this.addLog(playerIdx, 'abilityTrigger', card.instanceId,
            `${this.getCardName(card)}を除外`);
        }
        
        this.isWaitingForMove = false;
      });
    } else {
      // コールバックなし: 自動処理(パワー低い順に除外)
      const sorted = [...benchCards].sort((a, b) => a.currentPower - b.currentPower);
      const toRemove = sorted.slice(0, maxSelect);
      
      for (const card of toRemove) {
        const ownerIdx = (card as CardWithOwner)._ownerIdx;
        this.removeFromBench(ownerIdx, card);
        this.state.players[ownerIdx].field.exile.push(card);
        this.addLog(playerIdx, 'abilityTrigger', card.instanceId,
          `${this.getCardName(card)}を除外`);
      }
    }
  }

  // ベンチからカードを1枚除去
  private removeFromBench(playerIdx: number, card: CardInstance): void {
    const field = this.getInternalField(playerIdx);
    
    for (const slot of field._benchSlots) {
      const idx = slot.cards.findIndex(c => c.instanceId === card.instanceId);
      if (idx !== -1) {
        slot.cards.splice(idx, 1);
        // スロットが空になったら削除
        if (slot.cards.length === 0) {
          const slotIdx = field._benchSlots.indexOf(slot);
          field._benchSlots.splice(slotIdx, 1);
        }
        break;
      }
    }
    
    // bench配列も更新
    const benchIdx = this.state.players[playerIdx].field.bench.findIndex(
      c => c.instanceId === card.instanceId
    );
    if (benchIdx !== -1) {
      this.state.players[playerIdx].field.bench.splice(benchIdx, 1);
    }
  }

  // 条件チェック
  private checkCondition(
    playerIdx: number,
    condition?: AbilityCondition
  ): boolean {
    if (!condition || condition.type === 'none') return true;

    const field = this.getInternalField(playerIdx);

    switch (condition.type) {
      case 'categoryCount': {
        let count = 0;
        for (const slot of field._benchSlots) {
          const slotDef = this.cardDefs.get(slot.defId);
          if (slotDef?.category === condition.category) {
            count += slot.cards.length;
          }
        }
        return count >= (condition.minCount ?? 1);
      }
      case 'benchCount':
        return field._benchSlots.length >= (condition.minCount ?? 1);
      case 'flagHolder':
        return field.flagHolder !== null;
      default:
        return true;
    }
  }

  private endMatch(winnerIdx: number, reason: 'deckout' | 'benchFull'): void {
    this.state.status = 'finished';
    this.state.winner = this.state.players[winnerIdx].playerId;

    const loserIdx = winnerIdx === 0 ? 1 : 0;
    const reasonText = reason === 'deckout' 
      ? 'デッキ切れ' 
      : 'ベンチ満杯';

    // マッチ終了サマリー
    const p1Field = this.getInternalField(0);
    const p2Field = this.getInternalField(1);
    console.log(`[Battle] ========== MATCH END ==========`);
    console.log(`[Battle] Winner: P${winnerIdx + 1} (${this.state.players[winnerIdx].playerId.slice(-4)})`);
    console.log(`[Battle] Reason: ${reasonText}`);
    console.log(`[Battle] Total Turns: ${this.state.turnCount}`);
    console.log(`[Battle] P1 Final: deck=${p1Field._deck.length} bench=${p1Field._benchSlots.length}`);
    console.log(`[Battle] P2 Final: deck=${p2Field._deck.length} bench=${p2Field._benchSlots.length}`);
    console.log(`[Battle] =================================`);

    this.addLog(winnerIdx, 'matchEnd', undefined,
      `マッチ終了! ${this.state.players[loserIdx].playerId}が${reasonText}で敗北!`);
  }

  private addLog(
    playerIdx: number,
    action: BattleAction,
    cardId: string | undefined,
    message: string
  ): void {
    const playerId = this.state.players[playerIdx].playerId;
    const playerName = playerIdx === 0 ? 'P1' : 'P2';
    
    // コンソールに詳細ログ出力 ([Battle]プレフィックスでフィルタ可能)
    console.log(`[Battle] T${this.state.turnCount} ${playerName}(${playerId.slice(-4)}) [${action}] ${message}`);
    
    this.state.log.push({
      turn: this.state.turnCount,
      action,
      playerId,
      cardId,
      message,
    });
  }

  private getCardName(card: CardInstance): string {
    const def = this.cardDefs.get(card.defId);
    return def?.name ?? '???';
  }

  getState(): MatchState {
    return this.state;
  }

  // ベンチスロット情報取得 (UI用)
  getBenchSlots(playerIdx: number): { defId: string; name: string; count: number }[] {
    const field = this.getInternalField(playerIdx);
    return field._benchSlots.map((slot) => {
      const def = this.cardDefs.get(slot.defId);
      return {
        defId: slot.defId,
        name: def?.name ?? '???',
        count: slot.cards.length,
      };
    });
  }

  // 除外ゾーンの枚数取得 (UI用)
  getExileCount(playerIdx: number): number {
    return this.state.players[playerIdx].field.exile.length;
  }

  // 現在のアタックスタック取得 (UI用)
  getAttackStack(playerIdx: number): CardInstance[] {
    const field = this.getInternalField(playerIdx);
    return [...field._attackStack];
  }

  getResult(): MatchResult | null {
    if (this.state.status !== 'finished' || !this.state.winner) {
      return null;
    }

    const winnerId = this.state.winner;
    const loserId = this.state.players.find((p) => p.playerId !== winnerId)!.playerId;
    
    // 勝者がフラッグを奪った回数 = 敗者のベンチスロット数
    const loserIdx = this.state.players.findIndex((p) => p.playerId === loserId);
    const loserField = this.getInternalField(loserIdx);
    const winnerFlags = loserField._benchSlots.length;

    return {
      matchId: this.state.matchId,
      round: this.state.round,
      winnerId,
      loserId,
      winnerFansGained: FANS_PER_ROUND[this.state.round] ?? 1,
      winnerFlags,
      finalState: this.state,
    };
  }

  isFinished(): boolean {
    return this.state.status === 'finished';
  }

  // 自動進行 (全ターン処理)
  runToCompletion(): MatchResult | null {
    this.start();
    
    let safetyCounter = 0;
    const maxTurns = 200;
    
    while (!this.isFinished()) {
      this.processTurn();
      safetyCounter++;
      
      if (safetyCounter > maxTurns) {
        console.error('Match exceeded max turns, forcing end');
        // 現在フラッグを持っている方が勝ち
        const flagHolderIdx = this.state.players.findIndex((p) => p.field.flagHolder !== null);
        if (flagHolderIdx !== -1) {
          this.endMatch(flagHolderIdx, 'deckout');
        } else {
          this.endMatch(0, 'deckout');
        }
        break;
      }
    }

    return this.getResult();
  }
}

// 先行プレイヤー決定ヘルパー
export function determineFirstPlayer(
  player1Id: string,
  player1Trophies: number[],  // 獲得したラウンド番号のリスト
  player2Id: string,
  player2Trophies: number[]
): string {
  // 最高ラウンド番号のトロフィーを比較
  const max1 = player1Trophies.length > 0 ? Math.max(...player1Trophies) : 0;
  const max2 = player2Trophies.length > 0 ? Math.max(...player2Trophies) : 0;

  if (max1 > max2) return player1Id;
  if (max2 > max1) return player2Id;
  
  // 同点ならコイントス
  return Math.random() < 0.5 ? player1Id : player2Id;
}
